import { useMemo, useState } from "react";
import { Calculator } from "lucide-react";

const fmt = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export default function MortgageCalculator({ price = 500000 }) {
  const [downPct, setDownPct] = useState(20);
  const [rate, setRate] = useState(6.75);
  const [term, setTerm] = useState(30);
  const [taxRate, setTaxRate] = useState(2.5); // Texas avg ~2.2-2.7%
  const [insurance, setInsurance] = useState(1800);
  const [hoa, setHoa] = useState(0);

  const { monthly, breakdown } = useMemo(() => {
    const downPayment = (price * downPct) / 100;
    const loan = Math.max(price - downPayment, 0);
    const i = rate / 100 / 12;
    const n = term * 12;
    let pi = 0;
    if (loan > 0 && i > 0) {
      pi = (loan * i * Math.pow(1 + i, n)) / (Math.pow(1 + i, n) - 1);
    } else if (loan > 0) {
      pi = loan / n;
    }
    const taxesM = (price * (taxRate / 100)) / 12;
    const insM = insurance / 12;
    const hoaM = Number(hoa) || 0;
    const total = pi + taxesM + insM + hoaM;
    return {
      monthly: total,
      breakdown: {
        pi,
        taxes: taxesM,
        insurance: insM,
        hoa: hoaM,
        loan,
        downPayment,
      },
    };
  }, [price, downPct, rate, term, taxRate, insurance, hoa]);

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-7" data-testid="mortgage-calculator">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-[#E63946]/10 text-[#E63946] flex items-center justify-center">
          <Calculator className="w-5 h-5" strokeWidth={1.75} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#E63946]">Mortgage Calculator</div>
          <h3 className="font-display text-2xl font-semibold">Monthly payment estimate</h3>
        </div>
      </div>

      <div className="bg-stone-50 rounded-xl p-6 mb-6 text-center">
        <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2">Estimated monthly</div>
        <div className="font-display text-5xl font-semibold tracking-tighter text-[#1A1A1A]">
          {fmt(monthly)}
        </div>
        <div className="text-xs text-gray-500 mt-2">
          Loan amount {fmt(breakdown.loan)} · Down {fmt(breakdown.downPayment)}
        </div>
      </div>

      <div className="space-y-5">
        <Slider
          label={`Down payment · ${downPct}%`}
          value={downPct}
          onChange={setDownPct}
          min={0} max={50} step={1}
          testId="calc-down"
        />
        <Slider
          label={`Interest rate · ${rate.toFixed(2)}%`}
          value={rate}
          onChange={setRate}
          min={3} max={10} step={0.05}
          testId="calc-rate"
        />
        <Slider
          label={`Loan term · ${term} years`}
          value={term}
          onChange={setTerm}
          min={10} max={30} step={5}
          testId="calc-term"
        />

        <div className="grid grid-cols-3 gap-3 pt-2">
          <NumField
            label="Tax rate %"
            value={taxRate}
            step={0.1}
            onChange={setTaxRate}
            testId="calc-taxrate"
          />
          <NumField
            label="Insurance / yr"
            value={insurance}
            step={100}
            onChange={setInsurance}
            testId="calc-insurance"
          />
          <NumField
            label="HOA / mo"
            value={hoa}
            step={25}
            onChange={setHoa}
            testId="calc-hoa"
          />
        </div>
      </div>

      <div className="mt-7 pt-5 border-t border-gray-100 grid grid-cols-2 gap-y-2 text-sm">
        <Row label="Principal & Interest" value={fmt(breakdown.pi)} />
        <Row label="Property Taxes" value={fmt(breakdown.taxes)} />
        <Row label="Insurance" value={fmt(breakdown.insurance)} />
        <Row label="HOA" value={fmt(breakdown.hoa)} />
      </div>

      <p className="text-xs text-gray-400 mt-5 leading-relaxed">
        Estimate only. Actual rates and taxes vary. Contact a Gens Realty agent for a personalized financing roadmap.
      </p>
    </div>
  );
}

function Slider({ label, value, onChange, min, max, step, testId }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2">{label}</div>
      <input
        type="range"
        min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[#E63946]"
        data-testid={testId}
      />
    </label>
  );
}

function NumField({ label, value, onChange, step, testId }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-1.5">{label}</div>
      <input
        type="number"
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full bg-stone-50 border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#E63946]"
        data-testid={testId}
      />
    </label>
  );
}

function Row({ label, value }) {
  return (
    <>
      <div className="text-gray-500">{label}</div>
      <div className="text-right font-medium text-[#1A1A1A]">{value}</div>
    </>
  );
}
